<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">{{ editing ? 'Editar tipo de planilla' : 'Nuevo tipo de planilla' }}</h1>
    </div>
    <form class="custom-form" @submit.prevent="save" novalidate>
      <wz-card :title="editing ? 'Datos del tipo de planilla' : 'Datos del nuevo tipo de planilla'">
          <section class="form-section mb-0">
            <div class="flex flex-wrap -mx-2">
              <div v-if="editing" class="w-full md:w-1/2 px-2">
                <div class="mb-3">
                  <wz-input label="Código" v-model="form.code" :disabled="true" />
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3">
                  <wz-input
                    label="Nombre"
                    required
                    v-model="form.name"
                    placeholder="Ej: Planilla mensual"
                    :error="$v.form.name.$error && !$v.form.name.required ? 'Nombre es requerido' : ''"
                  />
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3">
                  <wz-select
                    id="frequency"
                    label="Frecuencia"
                    required
                    v-model="form.frequency"
                    :items="frequencyList"
                    defaultOption="Elegir frecuencia"
                    :error="$v.form.frequency.$error && !$v.form.frequency.required ? 'Frecuencia es requerida' : ''"
                  />
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3">
                  <wz-select
                    id="taxCategory"
                    label="Categoría tributaria"
                    required
                    v-model="form.taxCategory"
                    :items="taxCategoryList"
                    :disabled="editing"
                    defaultOption="Elegir categoría"
                    @change="onTaxCategoryChange"
                    :error="$v.form.taxCategory.$error && !$v.form.taxCategory.required ? 'Categoría es requerida' : ''"
                  />
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3">
                  <wz-select
                    id="calculationMode"
                    label="Modo de cálculo"
                    required
                    v-model="form.calculationMode"
                    :items="calculationModeList"
                    :disabled="editing"
                    defaultOption="Elegir modo"
                    :error="$v.form.calculationMode.$error && !$v.form.calculationMode.required ? 'Modo de cálculo es requerido' : ''"
                  />
                </div>
              </div>

              <!-- Toggles -->
              <div class="w-full px-2">
                <hr />
                <h6 class="text-muted mb-3">Configuración de conceptos</h6>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3 flex items-center">
                  <wz-checkbox v-model="form.affectsSocialBenefits" switch size="lg" class="mr-2" />
                  <label class="mb-0">Afecta beneficios sociales</label>
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3 flex items-center">
                  <wz-checkbox v-model="form.includesFamilyAllowance" switch size="lg" class="mr-2" @change="onFlagChange('includesFamilyAllowance')" />
                  <label class="mb-0">Incluye asignación familiar</label>
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3 flex items-center">
                  <wz-checkbox v-model="form.includesPension" switch size="lg" class="mr-2" @change="onFlagChange('includesPension')" />
                  <label class="mb-0">Incluye pensión</label>
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3 flex items-center">
                  <wz-checkbox v-model="form.includesEssalud" switch size="lg" class="mr-2" @change="onFlagChange('includesEssalud')" />
                  <label class="mb-0">Incluye EsSalud</label>
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3 flex items-center">
                  <wz-checkbox v-model="form.includesIr" switch size="lg" class="mr-2" @change="onFlagChange('includesIr')" />
                  <label class="mb-0">Incluye impuesto a la renta</label>
                </div>
              </div>

              <div class="w-full px-2"><hr /></div>

              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3">
                  <wz-input
                    label="Regla de pago"
                    v-model="form.payDayRule"
                    placeholder="Ej: Último día hábil del mes"
                  />
                </div>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <div class="mb-3 mb-0">
                  <wz-textarea
                    label="Descripción"
                    v-model="form.description"
                    :rows="2"
                    placeholder="Descripción opcional"
                  />
                </div>
              </div>
            </div>
          </section>
          <div class="mb-3 text-right">
            <wz-button variant="light" type="button" @click="$router.push({ name: 'payroll-type-list' })">{{ editing ? 'Volver' : 'Cancelar' }}</wz-button>
            <wz-button type="submit" variant="primary" class="ml-2">Guardar</wz-button>
          </div>
      </wz-card>
    </form>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onFlagConfirm"
      @cancel="onFlagCancel"
    />
  </div>
</template>

<script>
import { Alert, ConfirmModalMixin } from "@wizepe/wizeui"
import { PayrollTypeService } from '@/core/services'
import { Constants } from '@/core/utils'
import { required } from 'vuelidate/lib/validators'

export default {
  mixins: [ConfirmModalMixin],

  data() {
    return {
      form: {
        code: null,
        name: null,
        frequency: null,
        taxCategory: null,
        calculationMode: null,
        affectsSocialBenefits: true,
        includesFamilyAllowance: true,
        includesPension: true,
        includesEssalud: true,
        includesIr: true,
        payDayRule: null,
        description: null,
      },
      editing: false,
      pendingFlag: null,
      frequencyList: Object.values(Constants.PayrollFrequency),
      taxCategoryList: Object.values(Constants.PayrollTaxCategory),
      calculationModeList: Object.values(Constants.PayrollCalculationMode),
    }
  },
  validations: {
    form: {
      name: { required },
      frequency: { required },
      taxCategory: { required },
      calculationMode: { required },
    },
  },
  watch: { '$route.params.id'() { if (this.$route.params.id) this.load(this.$route.params.id) } },
  methods: {
    // Pre-marca de flags de aporte al cambiar la categoría (solo al cambiar; RRHH ajusta luego).
    onTaxCategoryChange() {
      const active = this.form.taxCategory === 'FIFTH'
      this.form.includesPension = active
      this.form.includesEssalud = active
      this.form.includesIr = active
      this.form.includesFamilyAllowance = active
    },
    flagLabel(flag) {
      return {
        includesPension: 'el aporte a pensión (AFP/ONP)',
        includesEssalud: 'EsSalud',
        includesIr: 'la retención de IR de 5ta categoría',
        includesFamilyAllowance: 'la asignación familiar',
      }[flag] || 'este aporte'
    },
    // Alerta informativa (no bloquea) al desactivar un aporte en un tipo de 5ta categoría.
    onFlagChange(flag) {
      if (this.form[flag] === false && this.form.taxCategory === 'FIFTH') {
        this.pendingFlag = flag
        this.openConfirmModal({
          title: 'Aporte de 5ta categoría',
          description: `Los conceptos remunerativos de 5ta categoría normalmente incluyen ${this.flagLabel(flag)}. ¿Está seguro que desea desactivarlo?`,
          acceptText: 'Sí, desactivar',
          cancelText: 'Volver a activar',
        })
      }
    },
    onFlagConfirm() {
      this.pendingFlag = null
      this.closeConfirmModal()
    },
    onFlagCancel() {
      if (this.pendingFlag) this.form[this.pendingFlag] = true
      this.pendingFlag = null
      this.closeConfirmModal()
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) return
      try {
        this.$store.dispatch('app/loading', true)
        let response
        if (!this.editing) response = await PayrollTypeService.save(this.form)
        else response = await PayrollTypeService.update(this.$route.params.id, this.form)
        Alert.success('Se almaceno la informacion correctamente')
        if (!this.editing) {
          this.$router.push({ name: 'payroll-type-edit', params: { id: response.payload.id } })
          this.editing = true
        }
      } catch (ex) {
        console.error(ex); Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally { this.$store.dispatch('app/loading', false) }
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await PayrollTypeService.get(id)
        this.form = { ...resp.payload }
        this.editing = true
      } catch (e) {
        console.error(e); Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'payroll-type-list' })
      } finally { this.$store.dispatch('app/loading', false) }
    },
  },
  created() { if (this.$route.params.id) this.load(this.$route.params.id) },
}
</script>
